/**
 * Navigation catalog (pure data + pure helpers, no React).
 *
 * Two views over the same set of routes:
 *   - NAV_GROUPS  : the legacy flat 6-group model (kept exported + unchanged so
 *                   the breadcrumb, the unit tests and any rollback keep working).
 *   - NAV_HUBS    : the 8-hub model rendered by the sidebar (E8 nav redesign).
 *                   Every NAV_GROUPS item AND every formerly-orphan route is
 *                   mapped into exactly one hub (see NAV_HUBS below + the
 *                   coverage test in DashboardLayout.model.test.tsx).
 */

import type { TranslationKey } from '@/i18n';

// Runtime import of the section-tab registry; `sectionTabs.ts` only imports
// TYPES from this module, so there is no evaluation cycle.
import { SECTION_TAB_ITEMS } from './sectionTabs';
import { tr } from '@/i18n';

export type IconName =
  | 'dashboard'
  | 'content'
  | 'calendar'
  | 'publishing'
  | 'platforms'
  | 'media'
  | 'analytics'
  | 'design'
  | 'workflows'
  | 'crm'
  | 'outreach'
  | 'email'
  | 'inbox'
  | 'projects'
  | 'ai-assistant'
  | 'ai-team'
  | 'orchestration'
  | 'studio'
  | 'repurpose'
  | 'hooks'
  | 'voice'
  | 'ab-test'
  | 'suggestions'
  | 'personalization'
  | 'community'
  | 'documents'
  | 'memory'
  | 'approvals'
  | 'ai-activity'
  | 'marketplace'
  | 'prospect'
  | 'discovery'
  | 'social'
  | 'listening'
  | 'ads'
  | 'bio'
  | 'settings';

// Nav labels are stored as i18n keys (nav.*) and resolved with `t(...)` at
// render, so the whole sidebar follows the active locale (and mirrors in RTL).
export interface NavItem {
  to: string;
  labelKey: TranslationKey;
  /**
   * Optional literal label that overrides the i18n key. Used for a brand-new
   * item whose catalog key does not exist yet (e.g. "Équipe 24/7"); everything
   * else stays i18n-driven via `labelKey`.
   */
  label?: string;
  icon: IconName;
  /** Hide for non-admin users (owner/admin/platform_superadmin see it). */
  adminOnly?: boolean;
  /** Plan feature flag; when the plan excludes it the item shows a lock badge. */
  feature?: string;
}

/**
 * A labelled sub-section inside a group (APG *disclosure navigation* pattern):
 * a nested `aria-expanded` disclosure button controlling a sub-`<ul>`. Used to
 * break the formerly-28-item flat "IA" group into a scannable multi-level tree.
 * `id` is a stable key (never shown) reused for the persisted collapse set and
 * for the `aria-controls` wiring. `label` is a literal (the shell's i18n
 * catalog is owned elsewhere and out of scope for this change).
 */
export interface NavSubGroup {
  id: string;
  label: string;
  items: NavItem[];
}

export interface NavGroup {
  labelKey: TranslationKey;
  /** Flat list of items (mutually exclusive with `subGroups`). */
  items?: NavItem[];
  /** Nested disclosure sub-sections (mutually exclusive with `items`). */
  subGroups?: NavSubGroup[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.groups.general',
    items: [
      { to: '/', labelKey: 'nav.items.dashboard', icon: 'dashboard' },
      // Gap I2 founder loop — Daily Moves + Product brief (i18n-driven labels).
      { to: '/today', labelKey: 'founder.nav.today', icon: 'dashboard' },
      // Autopilot from a URL — orchestrates the founder loop end to end.
      { to: '/autopilot', labelKey: 'autopilot.nav.label', icon: 'orchestration' },
      { to: '/strategy-mktg/brief', labelKey: 'founder.nav.brief', icon: 'documents' },
      { to: '/content', labelKey: 'nav.items.content', icon: 'content' },
      { to: '/recommandations', label: tr('shell.app.layoutsNavCatalog.aiRecommendations'), labelKey: 'nav.items.content', icon: 'ai-assistant' },
      // EPIC 7 — Marketing & Comms strategy engine. Literal label (no catalog
      // key yet); reuses an existing labelKey only as a type-safe fallback.
      { to: '/strategy-mktg', label: tr('shell.app.layoutsNavCatalog.marketingStrategy'), labelKey: 'nav.items.content', icon: 'analytics' },
      // I5 / R10 — Launch Kit (manual submissions only; no auto-post control).
      { to: '/strategy-mktg/launch-kit', labelKey: 'i5.nav.launchKit', icon: 'analytics' },
      // Multi-stage content approval config hub (chain + Slack/Teams notifications).
      // Literal label — no catalog key yet; the content review view is reached
      // per-content at /content/:id/review.
      // Intent-qualified: distinct from the AI approvals inbox at /approvals.
      { to: '/content/approvals', label: tr('shell.app.layoutsNavCatalog.contentApprovals'), labelKey: 'nav.items.content', icon: 'approvals' },
      // R13 — swipe deck over the nightly batch drafts (review queue).
      { to: '/content/deck', label: tr('shell.app.layoutsNavCatalog.validationDeck'), labelKey: 'nav.items.content', icon: 'approvals' },
      { to: '/calendar', labelKey: 'nav.items.calendar', icon: 'calendar' },
      // Epic 1 — agentic content engine. Literal labels (no catalog key yet; the
      // same additive pattern used by "Recommandations IA" / "Approbations" above).
      { to: '/planning/generate', label: tr('shell.app.layoutsNavCatalog.generateACalendar'), labelKey: 'nav.items.calendar', icon: 'calendar' },
      { to: '/planning/templates', label: tr('shell.app.layoutsNavCatalog.linkedinTemplates'), labelKey: 'nav.items.content', icon: 'content' },
      { to: '/planning/review', label: tr('shell.app.layoutsNavCatalog.aiReviewWorkshop'), labelKey: 'nav.items.content', icon: 'ai-assistant' },
      { to: '/publishing', labelKey: 'nav.items.publishing', icon: 'publishing' },
      { to: '/publishing/queue', labelKey: 'nav.items.contentQueue', icon: 'publishing' },
      { to: '/publishing/bulk', labelKey: 'nav.items.bulkSchedule', icon: 'publishing' },
      { to: '/publishing/studio', labelKey: 'nav.items.mediaStudio', icon: 'media' },
      { to: '/platforms', labelKey: 'nav.items.platforms', icon: 'platforms' },
      { to: '/ads', labelKey: 'nav.items.ads', icon: 'ads' },
      // Epic 4 — ads command-center (literal label; reuses the ads labelKey).
      { to: '/ads/command', label: tr('shell.app.layoutsNavCatalog.adsCommandCenter'), labelKey: 'nav.items.ads', icon: 'ads' },
      { to: '/media-library', labelKey: 'nav.items.mediaLibrary', icon: 'media' },
      { to: '/analytics', labelKey: 'nav.items.analytics', icon: 'analytics' },
      // Epic 6d — white-label reports + competitor benchmark. Literal label
      // (no catalog key yet); reuses the analytics icon.
      { to: '/reports', label: tr('shell.app.layoutsNavCatalog.reportsBenchmark'), labelKey: 'nav.items.analytics', icon: 'analytics' },
      // GEO — visibilité dans les moteurs de recherche IA (page /analytics/geo).
      { to: '/analytics/geo', label: tr('shell.app.layoutsNavCatalog.geoAiVisibility'), labelKey: 'nav.items.analytics', icon: 'analytics' },
      // I5 / R9 — verified-domain site audit (CRO + technical SEO + GEO).
      { to: '/analytics/site-audit', labelKey: 'i5.nav.siteAudit', icon: 'analytics' },
    ],
  },
  {
    labelKey: 'nav.groups.creation',
    items: [
      { to: '/design', labelKey: 'nav.items.design', icon: 'design' },
      { to: '/content/carousel', labelKey: 'nav.items.carouselEditor', icon: 'design' },
      // Idea feed (capture-URL + ranked feed). Literal label (no catalog key yet).
      { to: '/content/ideas', label: tr('shell.app.layoutsNavCatalog.ideaFeed'), labelKey: 'nav.items.content', icon: 'suggestions' },
      // Canonical "studio" entry. The video/audio studio previously sat here as
      // a second item re-using this same `richContentStudio` labelKey (a
      // duplicate label); it now lives only in the command palette
      // (id 'video-studio') so the sidebar carries one studio entry.
      // The ten "Studio…" surfaces share one "Studio · <intent>" naming so a
      // user scanning the tree can predict what each one opens.
      { to: '/content/studio', labelKey: 'nav.items.richContentStudio', icon: 'studio' },
      { to: '/content/diagram-studio', label: tr('shell.app.layoutsNavCatalog.studioDiagrams'), labelKey: 'nav.items.richContentStudio', icon: 'studio' },
      { to: '/content/unified', label: tr('shell.app.layoutsNavCatalog.studioAllInOne'), labelKey: 'nav.items.richContentStudio', icon: 'studio' },
      { to: '/content/source-studio', label: tr('shell.app.layoutsNavCatalog.studioSourcesNotebooklm'), labelKey: 'nav.items.richContentStudio', icon: 'documents' },
      {
        to: '/content/ai-studio',
        label: tr('shell.app.layoutsNavCatalog.studioAiMediaEditing'),
        labelKey: 'nav.items.richContentStudio',
        icon: 'studio',
      },
      { to: '/workflows', labelKey: 'nav.items.workflows', icon: 'workflows' },
      {
        to: '/workflows/studio',
        label: tr('shell.app.layoutsNavCatalog.studioFlowsBpmn'),
        labelKey: 'nav.items.workflows',
        icon: 'workflows',
      },
      { to: '/bio', labelKey: 'nav.items.bio', icon: 'bio' },
      { to: '/projects', labelKey: 'nav.items.projects', icon: 'projects' },
    ],
  },
  {
    labelKey: 'nav.groups.prospection',
    items: [
      { to: '/crm', labelKey: 'nav.items.crm', icon: 'crm' },
      { to: '/prospection', labelKey: 'nav.items.discovery', icon: 'discovery' },
      { to: '/prospection/offres', labelKey: 'nav.items.prospectionOffers', icon: 'prospect' },
      { to: '/prospection/recommend', labelKey: 'nav.items.prospectionRecommend', icon: 'prospect' },
      { to: '/prospection/account-intelligence', labelKey: 'nav.items.accountIntelligence', icon: 'prospect' },
      { to: '/prospection/calendar', labelKey: 'nav.items.prospectionCalendar', icon: 'calendar' },
      // Intent signals + inbound routing. Literal labels (no catalog key yet).
      { to: '/prospection/visitors', label: tr('shell.app.layoutsNavCatalog.identifiedVisitors'), labelKey: 'nav.items.prospect', icon: 'prospect' },
      { to: '/prospection/job-change-alerts', label: tr('shell.app.layoutsNavCatalog.jobChangeAlerts'), labelKey: 'nav.items.prospect', icon: 'prospect' },
      { to: '/prospection/inbound-routers', label: tr('shell.app.layoutsNavCatalog.inboundLeadRouting'), labelKey: 'nav.items.prospect', icon: 'prospect' },
      { to: '/prospect', labelKey: 'nav.items.prospect', icon: 'prospect' },
      { to: '/outreach', labelKey: 'nav.items.outreach', icon: 'outreach' },
      { to: '/marketplace', labelKey: 'nav.items.marketplace', icon: 'marketplace' },
    ],
  },
  {
    labelKey: 'nav.groups.engager',
    items: [
      { to: '/inbox', labelKey: 'nav.items.inbox', icon: 'inbox' },
      { to: '/community', labelKey: 'nav.items.community', icon: 'community' },
      { to: '/listening', labelKey: 'nav.items.listening', icon: 'listening' },
      // Gap R5 — conversation finder (copy-and-post-manually drafts).
      { to: '/listening/catches', label: tr('shell.app.layoutsNavCatalog.conversationsToJoin'), labelKey: 'nav.items.listening', icon: 'listening' },
      // Epic 6a — Veille / Listening dashboard (distinct from the inbox feed
      // above). Literal label (no catalog key yet); reuses the listening icon.
      { to: '/veille', label: tr('shell.app.layoutsNavCatalog.monitoringListening'), labelKey: 'nav.items.listening', icon: 'listening' },
      { to: '/social', labelKey: 'nav.items.social', icon: 'social' },
      // Epic 6g — influencer campaigns (shortlist → stage board → measurement).
      { to: '/influencers', label: tr('shell.app.layoutsNavCatalog.influencerCampaigns'), labelKey: 'nav.items.community', icon: 'social' },
      // Epic 6f — employee advocacy (feed + consent-gated tracked share, leaderboard, admin).
      { to: '/advocacy', label: tr('shell.app.layoutsNavCatalog.ambassadorsAdvocacy'), labelKey: 'nav.items.community', icon: 'social' },
      // Content Communities — authentic advocacy amplification (publisher + consented amplifiers).
      { to: '/communities', label: tr('shell.app.layoutsNavCatalog.contentCommunities'), labelKey: 'nav.items.community', icon: 'community' },
      // Gap R6 — account health & pacing levels.
      { to: '/communities/health', label: tr('shell.app.layoutsNavCatalog.accountHealth'), labelKey: 'nav.items.community', icon: 'community' },
    ],
  },
  {
    // The IA group was 28 flat siblings. It is now split into three labelled
    // disclosure sub-sections (APG disclosure-navigation) so the hierarchy is
    // scannable. The `/superadmin` ("Flotte plateforme") item was removed: it
    // is a *platform console* concern (platform-superadmin), not a tenant
    // sidebar entry, and belongs to the admin domain, not here.
    labelKey: 'nav.groups.ai',
    subGroups: [
      {
        id: 'ai.team247',
        label: tr('shell.app.layoutsNavCatalog.n247Team'),
        items: [
          // Was labelled exactly like its own sub-group header ("Équipe 24/7")
          // and carried the share-nodes glyph; now intent-qualified.
          { to: '/ai-team/hq', label: tr('shell.app.layoutsNavCatalog.n247CommandCenter'), labelKey: 'nav.items.aiTeam', icon: 'ai-team' },
          { to: '/ai-team', labelKey: 'nav.items.aiTeam', icon: 'ai-team' },
          { to: '/ai-team/office', labelKey: 'nav.items.aiOffice', icon: 'dashboard' },
          { to: '/ai-team/teams', labelKey: 'nav.items.aiTeams', icon: 'social' },
          {
            to: '/ai-team/builder',
            label: tr('shell.app.layoutsNavCatalog.teamBuilder'),
            labelKey: 'nav.items.aiTeams',
            icon: 'design',
          },
          {
            to: '/ai-team/templates',
            label: tr('shell.app.layoutsNavCatalog.teamTemplates'),
            labelKey: 'nav.items.aiTeams',
            icon: 'content',
          },
          {
            to: '/ai-team/pool',
            label: tr('shell.app.layoutsNavCatalog.taskQueue'),
            labelKey: 'nav.items.aiTeams',
            icon: 'inbox',
          },
          { to: '/ai-team/deliverables', labelKey: 'nav.items.aiDeliverables', icon: 'marketplace' },
          { to: '/ai-team/schedules', labelKey: 'nav.items.aiSchedules', icon: 'calendar' },
          { to: '/ai-team/control', labelKey: 'nav.items.aiControl', icon: 'settings' },
          { to: '/ai-team/audit', labelKey: 'nav.items.aiAuditTrail', icon: 'analytics' },
        ],
      },
      {
        id: 'ai.orchestration',
        label: tr('shell.app.layoutsNavCatalog.orchestrationAssistant'),
        items: [
          { to: '/orchestration', labelKey: 'nav.items.orchestration', icon: 'orchestration' },
          { to: '/ai-assistant', labelKey: 'nav.items.aiAssistant', icon: 'ai-assistant' },
          { to: '/approvals', labelKey: 'nav.items.approvals', icon: 'approvals' },
          { to: '/staff-questions', labelKey: 'nav.items.staffQuestions', icon: 'approvals' },
          { to: '/grants', labelKey: 'nav.items.grants', icon: 'approvals' },
          { to: '/ai-activity', labelKey: 'nav.items.aiActivity', icon: 'ai-activity' },
        ],
      },
      {
        id: 'ai.tools',
        label: tr('shell.app.layoutsNavCatalog.aiTools'),
        items: [
          { to: '/ai/content-studio', labelKey: 'nav.items.contentStudio', icon: 'studio' },
          { to: '/ai/ghostwriter', labelKey: 'nav.items.ghostwriter', icon: 'studio' },
          { to: '/ai/repurpose', labelKey: 'nav.items.repurpose', icon: 'repurpose' },
          { to: '/ai/hook-library', labelKey: 'nav.items.hookLibrary', icon: 'hooks' },
          { to: '/ai/longform', label: tr('shell.app.layoutsNavCatalog.studioLongFormArticles'), labelKey: 'nav.items.contentStudio', icon: 'studio' },
          { to: '/ai/canvas', label: tr('shell.app.layoutsNavCatalog.canvas'), labelKey: 'nav.items.contentStudio', icon: 'studio' },
          { to: '/ai/grid', label: tr('shell.app.layoutsNavCatalog.gridBulkGeneration'), labelKey: 'nav.items.contentStudio', icon: 'studio' },
          { to: '/ai/brand-voice', labelKey: 'nav.items.brandVoice', icon: 'voice' },
          { to: '/ai/ab-copy', labelKey: 'nav.items.abCopy', icon: 'ab-test' },
          { to: '/ai/suggestions', labelKey: 'nav.items.suggestions', icon: 'suggestions' },
          { to: '/ai/personalization', labelKey: 'nav.items.personalization', icon: 'personalization' },
          { to: '/ai/community-ai', labelKey: 'nav.items.communityAi', icon: 'community' },
          { to: '/ai/rag-documents', labelKey: 'nav.items.ragDocuments', icon: 'documents' },
          { to: '/ai/memory', labelKey: 'nav.items.memory', icon: 'memory' },
        ],
      },
    ],
  },
  {
    labelKey: 'nav.groups.system',
    items: [{ to: '/settings', labelKey: 'nav.items.settings', icon: 'settings' }],
  },
];

/** Every NavItem in a group, flattening any disclosure sub-sections. */
export function flattenNavItems(group: NavGroup): NavItem[] {
  if (group.subGroups) return group.subGroups.flatMap((s) => s.items);
  return group.items ?? [];
}

/** True when `to` is the active route for `pathname` (exact, or a path-prefix
 *  for non-root routes). Mirrors the matching NavLink uses. */
export function isNavRouteActive(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

export interface Crumb {
  label: string;
  isCurrent: boolean;
}

/**
 * Wayfinding breadcrumb trail (after the root crumb).
 *
 * `sections` are navigable items with already-resolved labels (the hub
 * catalog, which nests — `/settings` AND `/settings/mailboxes` are both
 * items). The trail is:
 *   1. the SHORTEST matching non-root section (the parent hub entry, e.g.
 *      "Paramètres"), when it differs from…
 *   2. the LONGEST matching section (the exact item, e.g. "Boîtes d’envoi"),
 *   3. then — when the URL is deeper than that — the active SUB-route's name
 *      resolved via `resolveSub` (command catalog + section-tab registry),
 *      e.g. `/crm/contacts` → `CRM` ▸ `Contacts`.
 * Sub-labels read like "CRM · Contacts"; the section prefix is stripped so only
 * the leaf ("Contacts") is shown, and a leaf that merely echoes a section is
 * dropped. A flat `sections` list (no nesting) yields the legacy 2-crumb trail.
 */
export function deriveBreadcrumb(
  pathname: string,
  sections: { to: string; label: string }[],
  resolveSub: (path: string) => string | null,
): Crumb[] {
  let longest: { to: string; label: string } | undefined;
  let shortest: { to: string; label: string } | undefined;
  for (const s of sections) {
    if (!isNavRouteActive(s.to, pathname)) continue;
    if (!longest || s.to.length > longest.to.length) longest = s;
    if (s.to !== '/' && (!shortest || s.to.length < shortest.to.length)) shortest = s;
  }
  if (!longest) return [];

  const crumbs: Crumb[] = [];
  if (shortest && shortest.to !== longest.to) {
    crumbs.push({ label: shortest.label, isCurrent: false });
  }
  crumbs.push({ label: longest.label, isCurrent: false });

  if (pathname !== longest.to && pathname.startsWith(longest.to)) {
    const sub = resolveSub(pathname);
    if (sub) {
      const leaf = (sub.includes('·') ? sub.split('·').pop()! : sub).trim();
      const echoes = crumbs.some((c) => c.label.toLowerCase() === leaf.toLowerCase());
      if (leaf && !echoes) crumbs.push({ label: leaf, isCurrent: false });
    }
  }
  // The deepest named crumb is the current location.
  crumbs[crumbs.length - 1].isCurrent = true;
  return crumbs;
}

// ===========================================================================
// E8 — 8-hub navigation model.
// ===========================================================================

/**
 * Routes that exist in the router but had NO sidebar entry before the redesign
 * ("orphans": reachable only through the palette, a tab strip or a deep link).
 * They are surfaced inside their natural hub. Literal labels (the shell i18n
 * catalog is owned elsewhere), reusing an existing labelKey as the type fallback.
 */
export const ORPHAN_ITEMS: NavItem[] = [
  // Documents hub — received/issued invoices + business documents, classified
  // and filterable by month/vendor/category/type/status/source.
  { to: '/documents', label: tr('shell.app.layoutsNavCatalog.documentsInvoices'), labelKey: 'nav.items.content', icon: 'documents' },
  { to: '/content/new', label: tr('shell.app.layoutsNavCatalog.newContent'), labelKey: 'nav.items.content', icon: 'content' },
  { to: '/content/video-studio', label: tr('shell.app.layoutsNavCatalog.studioVideoAudio'), labelKey: 'nav.items.richContentStudio', icon: 'studio' },
  { to: '/design/brand-kits', label: tr('shell.app.layoutsNavCatalog.brandKits'), labelKey: 'nav.items.design', icon: 'design' },
  { to: '/publishing/history', label: tr('shell.app.layoutsNavCatalog.publishingHistory'), labelKey: 'nav.items.publishing', icon: 'publishing' },
  { to: '/publishing/wizard', label: tr('shell.app.layoutsNavCatalog.publishingAssistant'), labelKey: 'nav.items.publishing', icon: 'publishing' },
  { to: '/ads/ai-create', label: tr('shell.app.layoutsNavCatalog.expressAdCreationAi'), labelKey: 'nav.items.ads', icon: 'ads' },
  { to: '/prospection/strategy', label: tr('shell.app.layoutsNavCatalog.prospectingStrategy'), labelKey: 'nav.items.prospect', icon: 'discovery' },
  { to: '/analytics/monitoring', label: tr('shell.app.layoutsNavCatalog.monitoringAlerts'), labelKey: 'nav.items.analytics', icon: 'analytics' },
  { to: '/analytics/attribution', label: tr('shell.app.layoutsNavCatalog.attributionRoi'), labelKey: 'nav.items.analytics', icon: 'analytics' },
  { to: '/automations', label: tr('shell.app.layoutsNavCatalog.automations'), labelKey: 'nav.items.workflows', icon: 'workflows' },
  { to: '/learning', label: tr('shell.app.layoutsNavCatalog.aiLearning'), labelKey: 'nav.items.aiActivity', icon: 'memory' },
  { to: '/inbox/automation', label: tr('shell.app.layoutsNavCatalog.inboxAutomation'), labelKey: 'nav.items.inbox', icon: 'inbox' },
  { to: '/community/canned', label: tr('shell.app.layoutsNavCatalog.repliesMacros'), labelKey: 'nav.items.community', icon: 'community' },
  { to: '/community/sla', label: tr('shell.app.layoutsNavCatalog.slaSatisfaction'), labelKey: 'nav.items.community', icon: 'community' },
  { to: '/crm/pipeline', label: tr('shell.app.layoutsNavCatalog.salesPipeline'), labelKey: 'nav.items.crm', icon: 'crm' },
  { to: '/crm/signals', label: tr('shell.app.layoutsNavCatalog.intentSignals'), labelKey: 'nav.items.crm', icon: 'crm' },
  { to: '/outreach/unibox', label: tr('shell.app.layoutsNavCatalog.uniboxReplies'), labelKey: 'nav.items.outreach', icon: 'outreach' },
  // Settings sub-pages (previously only reachable from the Settings tab strip).
  { to: '/settings/profile', label: tr('shell.app.layoutsNavCatalog.profile'), labelKey: 'nav.items.settings', icon: 'settings' },
  { to: '/settings/team', label: tr('shell.app.layoutsNavCatalog.teamInvitations'), labelKey: 'nav.items.settings', icon: 'crm' },
  { to: '/settings/organization', label: tr('shell.app.layoutsNavCatalog.organization'), labelKey: 'nav.items.settings', icon: 'settings' },
  { to: '/settings/notifications', label: tr('shell.app.layoutsNavCatalog.notifications'), labelKey: 'nav.items.settings', icon: 'settings' },
  { to: '/settings/workspaces', label: tr('shell.app.layoutsNavCatalog.workspaces'), labelKey: 'nav.items.settings', icon: 'settings', adminOnly: true },
  { to: '/settings/roles', label: tr('shell.app.layoutsNavCatalog.rolesPermissions'), labelKey: 'nav.items.settings', icon: 'approvals', adminOnly: true },
  { to: '/settings/connections', label: tr('shell.app.layoutsNavCatalog.networkConnections'), labelKey: 'nav.items.platforms', icon: 'platforms' },
  { to: '/settings/connectors', label: tr('shell.app.layoutsNavCatalog.connectors'), labelKey: 'nav.items.settings', icon: 'platforms' },
  { to: '/settings/integrations', label: tr('shell.app.layoutsNavCatalog.integrations'), labelKey: 'nav.items.settings', icon: 'platforms', feature: 'byo_integrations' },
  { to: '/settings/developers', label: tr('shell.app.layoutsNavCatalog.apiDevelopers'), labelKey: 'nav.items.settings', icon: 'hooks' },
  { to: '/settings/mailboxes', label: tr('shell.app.layoutsNavCatalog.sendingMailboxes'), labelKey: 'nav.items.settings', icon: 'outreach' },
  { to: '/settings/subscription', label: tr('shell.app.layoutsNavCatalog.subscription'), labelKey: 'nav.items.settings', icon: 'marketplace' },
  { to: '/settings/billing', label: tr('shell.app.layoutsNavCatalog.billing'), labelKey: 'nav.items.settings', icon: 'marketplace' },
  { to: '/settings/credits', label: tr('shell.app.layoutsNavCatalog.aiCredits'), labelKey: 'nav.items.settings', icon: 'ai-activity' },
  { to: '/settings/credits/budgets', label: tr('shell.app.layoutsNavCatalog.perUserBudgets'), labelKey: 'nav.items.settings', icon: 'ai-activity', adminOnly: true },
  { to: '/settings/referral', label: tr('shell.app.layoutsNavCatalog.referral'), labelKey: 'nav.items.settings', icon: 'social' },
  { to: '/settings/ai-registry', label: tr('shell.app.layoutsNavCatalog.aiRegistry'), labelKey: 'nav.items.settings', icon: 'documents' },
  { to: '/settings/models', label: tr('shell.app.layoutsNavCatalog.aiModelsAdmin'), labelKey: 'nav.items.settings', icon: 'ai-assistant', adminOnly: true },
  { to: '/settings/byok', label: tr('shell.app.layoutsNavCatalog.llmKeysByok'), labelKey: 'nav.items.settings', icon: 'hooks', feature: 'byok_llm' },
  { to: '/settings/governance', label: tr('shell.app.layoutsNavCatalog.aiGovernance'), labelKey: 'nav.items.settings', icon: 'approvals', adminOnly: true },
  { to: '/settings/content-engine', label: tr('shell.app.layoutsNavCatalog.contentEngine'), labelKey: 'nav.items.settings', icon: 'content' },
  { to: '/settings/review-policy', label: tr('shell.app.layoutsNavCatalog.reviewPolicy'), labelKey: 'nav.items.settings', icon: 'approvals' },
  { to: '/settings/trust-center', label: tr('shell.app.layoutsNavCatalog.trustCenter'), labelKey: 'nav.items.settings', icon: 'approvals' },
  // R15 / R16 (gap I6b): programs for the tenant's own product + in-app checklists.
  { to: '/growth/programs', label: tr('shell.app.layoutsNavCatalog.partnerPrograms'), labelKey: 'nav.items.programs', icon: 'social' },
  { to: '/analytics/checklists', label: tr('shell.app.layoutsNavCatalog.inAppChecklists'), labelKey: 'nav.items.checklists', icon: 'analytics' },
];

/** Plan-feature / admin metadata for legacy items (keyed by route). */
const ITEM_META: Record<string, Pick<NavItem, 'adminOnly' | 'feature'>> = {
  '/ai-team': { feature: 'ai_teams' },
  '/ai-team/hq': { feature: 'ai_teams' },
  '/ai-team/office': { feature: 'ai_teams' },
  '/ai-team/teams': { feature: 'ai_teams' },
  '/ai-team/builder': { feature: 'ai_teams' },
  '/ai-team/templates': { feature: 'ai_teams' },
  '/ai-team/pool': { feature: 'ai_teams' },
  '/automations': { feature: 'automations' },
  '/marketplace': { feature: 'marketplace' },
  '/content/studio': { feature: 'content_studio' },
  '/content/ai-studio': { feature: 'content_studio' },
  '/content/unified': { feature: 'content_studio' },
  '/content/source-studio': { feature: 'content_studio' },
  '/ai/content-studio': { feature: 'content_studio' },
  '/reports': { feature: 'advanced_analytics' },
  '/analytics/attribution': { feature: 'advanced_analytics' },
  // Both pages gate themselves on the same flag (AnalyticsLayout ADVANCED_TABS
  // + GeoHubPage); the badge must say so instead of looking unlocked.
  '/analytics/geo': { feature: 'advanced_analytics' },
  '/analytics/monitoring': { feature: 'advanced_analytics' },
  '/listening/catches': { feature: 'community_catches' },
  // I5 launch stage — plan flags seeded in ai-orchestrator DEFAULT_PLANS +
  // backfilled onto existing plan rows by migration i5_014_plan_features.
  '/analytics/site-audit': { feature: 'site_audit' },
  '/strategy-mktg/launch-kit': { feature: 'launch_kit' },
  '/autopilot': { feature: 'autopilot' },
  '/growth/programs': { feature: 'automations' },
  '/analytics/checklists': { feature: 'automations' },
};

const ITEM_LOOKUP: Map<string, NavItem> = new Map();
for (const it of [...NAV_GROUPS.flatMap(flattenNavItems), ...ORPHAN_ITEMS]) {
  if (!ITEM_LOOKUP.has(it.to)) ITEM_LOOKUP.set(it.to, { ...it, ...ITEM_META[it.to] });
}

/** Resolve a route to its NavItem (a bare fallback if the catalog lacks it —
 *  the coverage unit test fails loudly on that, the UI never white-screens). */
function r(to: string): NavItem {
  return ITEM_LOOKUP.get(to) ?? { to, label: to, labelKey: 'nav.items.content', icon: 'content' };
}

export interface NavHub {
  /** Stable key (persisted collapse state, aria ids). */
  id: string;
  label: string;
  icon: IconName;
  /** Flat list (mutually exclusive with `subGroups`). */
  items?: NavItem[];
  subGroups?: NavSubGroup[];
}

const sg = (id: string, label: string, routes: string[]): NavSubGroup => ({
  id,
  label,
  items: routes.map(r),
});

export const NAV_HUBS: NavHub[] = [
  {
    id: 'hub.home',
    label: tr('shell.app.layoutsNavCatalog.home'),
    icon: 'dashboard',
    items: ['/', '/today', '/autopilot', '/recommandations', '/strategy-mktg', '/strategy-mktg/brief', '/strategy-mktg/launch-kit', '/projects', '/documents'].map(r),
  },
  {
    id: 'hub.content',
    label: tr('shell.app.layoutsNavCatalog.content'),
    icon: 'content',
    subGroups: [
      sg('content.create', tr('shell.app.layoutsNavCatalog.create'), [
        '/content', '/content/new', '/content/ideas', '/content/studio', '/content/ai-studio',
        '/content/video-studio', '/content/diagram-studio', '/content/unified', '/content/source-studio',
        '/content/carousel', '/design', '/design/brand-kits', '/bio',
      ]),
      sg('content.plan', 'Planifier & publier', [
        '/calendar', '/planning/generate', '/planning/templates', '/planning/review', '/publishing',
        '/publishing/queue', '/publishing/bulk', '/publishing/studio', '/publishing/wizard',
        '/publishing/history', '/platforms',
      ]),
      sg('content.library', tr('shell.app.layoutsNavCatalog.libraryApproval'), ['/media-library', '/content/approvals', '/content/deck']),
    ],
  },
  {
    id: 'hub.engage',
    label: tr('shell.app.layoutsNavCatalog.engagement'),
    icon: 'community',
    subGroups: [
      sg('engage.inbox', tr('shell.app.layoutsNavCatalog.inboxCommunity'), [
        '/inbox', '/inbox/automation', '/community', '/community/canned', '/community/sla',
      ]),
      sg('engage.listen', tr('shell.app.layoutsNavCatalog.listeningMonitoring'), ['/listening', '/listening/catches', '/veille', '/social']),
      sg('engage.influence', 'Influence', ['/influencers', '/advocacy', '/communities', '/communities/health']),
    ],
  },
  {
    // Email Automation Suite — a dedicated hub (connected mailboxes, AI-classed
    // inbox, triage rules + autonomy policy, the visual automation builder, and
    // reusable templates). Items carry explicit literal labels + the
    // `email_automation` plan flag; routes are brand-new (not in NAV_GROUPS).
    id: 'hub.email',
    label: tr('shell.app.layoutsNavCatalog.email'),
    icon: 'email',
    items: [
      { to: '/email/mailboxes', label: tr('shell.app.layoutsNavCatalog.connectedMailboxes'), labelKey: 'nav.items.outreach', icon: 'email', feature: 'email_automation' },
      { to: '/email/inbox', label: tr('shell.app.layoutsNavCatalog.emailInbox'), labelKey: 'nav.items.inbox', icon: 'inbox', feature: 'email_automation' },
      { to: '/email/rules', label: tr('shell.app.layoutsNavCatalog.rulesAutomation'), labelKey: 'nav.items.workflows', icon: 'approvals', feature: 'email_automation' },
      // The visual builder runs on /workflow-definitions (gated `automations`), not the email suite.
      { to: '/email/builder', label: tr('shell.app.layoutsNavCatalog.visualBuilder'), labelKey: 'nav.items.workflows', icon: 'workflows', feature: 'automations' },
      { to: '/email/templates', label: tr('shell.app.layoutsNavCatalog.emailTemplates'), labelKey: 'nav.items.content', icon: 'content', feature: 'email_automation' },
    ],
  },
  {
    id: 'hub.growth',
    label: tr('shell.app.layoutsNavCatalog.growth'),
    icon: 'discovery',
    subGroups: [
      sg('growth.crm', 'CRM & leads', ['/crm', '/crm/pipeline', '/crm/signals', '/prospect']),
      sg('growth.prospect', tr('shell.app.layoutsNavCatalog.prospecting'), [
        '/prospection', '/prospection/offres', '/prospection/recommend',
        '/prospection/account-intelligence', '/prospection/calendar', '/prospection/strategy',
        '/prospection/visitors', '/prospection/job-change-alerts', '/prospection/inbound-routers',
      ]),
      sg('growth.outreach', tr('shell.app.layoutsNavCatalog.outreachMarket'), ['/outreach', '/outreach/unibox', '/marketplace']),
      sg('growth.programs', tr('shell.app.layoutsNavCatalog.partnerPrograms'), ['/growth/programs']),
    ],
  },
  {
    id: 'hub.ads',
    label: tr('shell.app.layoutsNavCatalog.ads'),
    icon: 'ads',
    items: ['/ads', '/ads/command', '/ads/ai-create'].map(r),
  },
  {
    id: 'hub.analytics',
    // Product term used everywhere else (page title, palette, first item);
    // the FR/EN mix "Analyses ▸ Analytics" read as two different things.
    label: tr('shell.app.layoutsNavCatalog.analytics'),
    icon: 'analytics',
    items: [
      '/analytics', '/reports', '/analytics/geo', '/analytics/site-audit', '/analytics/attribution', '/analytics/monitoring',
      '/analytics/checklists',
    ].map(r),
  },
  {
    id: 'hub.ai',
    label: tr('shell.app.layoutsNavCatalog.aiAutomation'),
    icon: 'ai-assistant',
    subGroups: [
      ...(NAV_GROUPS.find((g) => g.labelKey === 'nav.groups.ai')?.subGroups ?? []).map((s) => ({
        ...s,
        items: s.items.map((i) => r(i.to)),
      })),
      sg('ai.automation', 'Workflows & apprentissage', [
        '/workflows', '/workflows/studio', '/automations', '/learning',
      ]),
    ],
  },
  {
    id: 'hub.settings',
    label: tr('shell.app.layoutsNavCatalog.parameters'),
    icon: 'settings',
    subGroups: [
      sg('settings.account', tr('shell.app.layoutsNavCatalog.accountTeam'), [
        '/settings', '/settings/profile', '/settings/team', '/settings/organization',
        '/settings/notifications', '/settings/roles',
      ]),
      sg('settings.connect', tr('shell.app.layoutsNavCatalog.connectionsIntegrations'), [
        '/settings/connections', '/settings/connectors', '/settings/integrations',
        '/settings/developers', '/settings/mailboxes',
      ]),
      sg('settings.plan', tr('shell.app.layoutsNavCatalog.planCredits'), [
        '/settings/subscription', '/settings/billing', '/settings/credits',
        '/settings/credits/budgets', '/settings/referral',
      ]),
      sg('settings.ai', tr('shell.app.layoutsNavCatalog.aiCompliance'), [
        '/settings/ai-registry', '/settings/models', '/settings/byok', '/settings/governance',
        '/settings/content-engine', '/settings/review-policy', '/settings/trust-center',
      ]),
    ],
  },
];

/** Every item of a hub, flattening sub-sections. */
export function flattenHubItems(hub: NavHub): NavItem[] {
  return hub.subGroups ? hub.subGroups.flatMap((s) => s.items) : (hub.items ?? []);
}

/** All hub items (flat, in display order). */
export const ALL_HUB_ITEMS: NavItem[] = NAV_HUBS.flatMap(flattenHubItems);

/** Section-tab routes (tab strips only — never in the sidebar), keyed by route. */
const SECTION_TAB_LOOKUP: Map<string, NavItem> = new Map(SECTION_TAB_ITEMS.map((i) => [i.to, i]));

/**
 * Every navigable target: hub items first, then the section-tab routes that
 * are reachable only through an in-section strip. Used by the palette, the
 * Récents tracker and the breadcrumb so a tab route is a first-class
 * destination (searchable, pinnable) instead of collapsing to its hub.
 */
export const ALL_NAV_TARGETS: NavItem[] = [
  ...ALL_HUB_ITEMS,
  ...SECTION_TAB_ITEMS.filter((i) => !ITEM_LOOKUP.has(i.to)),
];

/** Look up a navigable item by exact route — hub items first, then section tabs. */
export function getNavItem(to: string): NavItem | undefined {
  return ITEM_LOOKUP.get(to) ?? SECTION_TAB_LOOKUP.get(to);
}

/**
 * The single best-matching hub item for `pathname` (longest `to` that is the
 * path or a path-prefix of it). Drives the sidebar's exact active highlight
 * (so `/settings` is not lit for `/settings/billing`) and the Récents tracker.
 */
export function matchNavItem(pathname: string, items: NavItem[] = ALL_HUB_ITEMS): NavItem | undefined {
  let best: NavItem | undefined;
  for (const it of items) {
    if (!isNavRouteActive(it.to, pathname)) continue;
    if (!best || it.to.length > best.to.length) best = it;
  }
  return best;
}
